import React, { useState, useContext, useEffect } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter
} from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ArcaContext } from '../../context/ArcaContext.context';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import saveIndexaToken from '../../api/thirdparty/indexa/saveIndexaToken';
import getIndexaToken from '../../api/thirdparty/indexa/getIndexaToken';
import toggleIndexaCapital from '../../api/thirdparty/indexa/toggleIndexaCapital';
import IndexaPlanSummaryV2 from './IndexaPlanSummaryV2';

interface IndexaCapitalConfigModalV2Props {
	isOpen: boolean;
	onClose: () => void;
}

const IndexaCapitalConfigModalV2: React.FC<IndexaCapitalConfigModalV2Props> = ({
	isOpen,
	onClose
}) => {
	const { token } = useContext(ArcaContext);
	const {
		state: { translate }
	} = useContext(I18nContext);
	const [apiKey, setApiKey] = useState<string>('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [currentToken, setCurrentToken] = useState<string | null>(null);
	const [isConnected, setIsConnected] = useState(false);
	const [createdAt, setCreatedAt] = useState<Date | null>(null);

	const handleSave = async () => {
		if (!apiKey.trim()) {
			return;
		}

		setIsSubmitting(true);
		try {
			const response = await saveIndexaToken(token, { indexaApiKey: apiKey });
			if (response.success) {
				onClose();
			} else {
				console.error('Error al guardar el token de Indexa Capital:', response.message);
			}
		} catch (error) {
			console.error('Error al guardar el token de Indexa Capital:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const maskApiKey = (key: string): string => {
		if (key.length <= 8) return key;
		return `${key.substring(0, 4)}...${key.substring(key.length - 4)}`;
	};

	useEffect(() => {
		const fetchCurrentToken = async () => {
			if (isOpen && token) {
				setIsLoading(true);
				try {
					const response = await getIndexaToken(token);
					if (response.success && response.data?.indexaApiKey) {
						setCurrentToken(response.data.indexaApiKey);
						setIsConnected(true);
						setCreatedAt(response.data.createdAt);
						const maskedKey = maskApiKey(response.data.indexaApiKey);
						setApiKey(maskedKey);
					} else {
						setIsConnected(false);
						setCurrentToken(null);
						setApiKey('');
						setCreatedAt(null);
					}
				} catch (error) {
					console.error('Error fetching current Indexa token:', error);
					setIsConnected(false);
					setCurrentToken(null);
					setApiKey('');
				} finally {
					setIsLoading(false);
				}
			}
		};

		fetchCurrentToken();
	}, [isOpen, token]);

	const handleDisconnect = async () => {
		try {
			const response = await toggleIndexaCapital(token);
			if (response.success) {
				setIsConnected(false);
				setCurrentToken(null);
				setApiKey('');
				setCreatedAt(null);
			} else {
				console.error('Error al desconectar de Indexa Capital:', response.message);
			}
		} catch (error) {
			console.error('Error al desconectar de Indexa Capital:', error);
		}
	};

	const isFormValid = () => {
		return apiKey.trim().length > 0 && !isConnected;
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>{translate('indexaCapital.modal.title')}</DialogTitle>
					<DialogDescription>{translate('indexaCapital.modal.description')}</DialogDescription>
				</DialogHeader>

				{isLoading ? (
					<div className="flex items-center justify-center py-8">
						<p className="text-sm text-muted-foreground">
							{translate('indexaCapital.modal.loading')}
						</p>
					</div>
				) : isConnected ? (
					<Tabs defaultValue="configuration">
						<TabsList>
							<TabsTrigger value="configuration" className="text-sm">
								{translate('indexaCapital.modal.configuration')}
							</TabsTrigger>
							<TabsTrigger value="contributions" className="text-sm">
								{translate('indexaCapital.modal.contributions')}
							</TabsTrigger>
						</TabsList>

						<TabsContent value="configuration" className="mt-4">
							<div className="flex flex-col gap-6">
								<div className="flex items-center gap-3 rounded-md border border-primary/20 bg-primary/5 p-4">
									<CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
									<p className="text-sm text-foreground">
										{translate('indexaCapital.modal.connected')}
									</p>
								</div>

								<div>
									<p className="text-sm font-medium mb-2">
										{translate('indexaCapital.modal.apiKeyConfigured')}
									</p>
									<p className="text-sm text-muted-foreground font-mono mb-2">{apiKey}</p>
									{createdAt && (
										<p className="text-sm text-muted-foreground">
											{translate('indexaCapital.modal.configuredOn')}{' '}
											{new Date(createdAt).toLocaleDateString('es-ES', {
												year: 'numeric',
												month: 'long',
												day: 'numeric',
												hour: '2-digit',
												minute: '2-digit'
											})}
										</p>
									)}
								</div>
							</div>
						</TabsContent>

						<TabsContent value="contributions" className="mt-4">
							{token ? <IndexaPlanSummaryV2 token={token} translate={translate} /> : null}
						</TabsContent>
					</Tabs>
				) : (
					<div className="flex flex-col gap-6">
						<div className="flex items-start gap-3 rounded-md border border-primary/20 bg-primary/5 p-4">
							<CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
							{/* min-w-0: sin él, este bloque no encoge por debajo de la dirección de ayuda —57
							    caracteres sin un sitio por donde partirse— y en un móvil ensancha el diálogo. */}
							<div className="flex min-w-0 flex-col gap-2">
								<p className="text-sm text-foreground">
									¡Tenemos integración directa con Indexa Capital! Introduce tu API_KEY para
									vincular tu plan automáticamente.
								</p>
								<a
									href="https://support.indexacapital.com/es/esp/introduccion-api"
									target="_blank"
									rel="noopener noreferrer"
									className="text-sm text-primary hover:underline"
								>
									https://support.indexacapital.com/es/esp/introduccion-api
								</a>
								<Input
									placeholder="API_KEY"
									value={apiKey}
									onChange={(e) => setApiKey(e.target.value)}
									// `focus-visible:ring-primary` fijaba el COLOR de un anillo cuyo ancho ponía el Input del
									// registry. Desde el 26/09/26 ese `ring-2` ya no está y el foco se marca con el
									// borde, así que la clase pintaba un anillo de 0 px: aparentaba marcar el foco
									// sin marcarlo.
									className="border-primary/20"
								/>
							</div>
						</div>
					</div>
				)}

				<DialogFooter className="flex flex-row gap-2">
					<Button
						variant="ghost"
						onClick={onClose}
						className="flex-1 rounded-full text-muted-foreground"
					>
						{translate('common.cancel')}
					</Button>
					{isConnected ? (
						<Button
							variant="destructive"
							onClick={handleDisconnect}
							className="flex-1 rounded-full"
						>
							{translate('indexaCapital.modal.disconnect')}
						</Button>
					) : (
						<Button
							onClick={handleSave}
							disabled={!isFormValid() || isSubmitting}
							className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full"
						>
							{isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
							{translate('indexaCapital.modal.saveConfiguration')}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default IndexaCapitalConfigModalV2;
